import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../components/AuthContext';
import axios from 'axios';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { toast } from 'react-toastify';
import { FaSearch, FaFilter, FaDownload, FaPlus, FaClock, FaUser, FaTimes, FaTag, FaBox, FaCheckCircle, FaCalendarAlt } from 'react-icons/fa';
import MasterSelection from '../../components/MasterSelection';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Pie } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const ActivityLogsNew = () => {
  const { user } = useContext(AuthContext);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterItem, setFilterItem] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [itemsPerPage] = useState(20);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    categories: [],
    items: [],
    statuses: []
  });
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: []
  });
  const [pieChartData, setPieChartData] = useState({
    labels: [],
    datasets: []
  });
  const [statistics, setStatistics] = useState({
    total: 0,
    pending: 0,
    completed: 0,
    cancelled: 0,
    draft: 0,
    service: 0,
    quotationIssued: 0
  });

  // Form state
  const [formData, setFormData] = useState({
    date: new Date(),
    category: '',
    items: [],
    guest: '',
    reference: '',
    customer: '',
    dealer: '',
    remarks: ''
  });

  // MasterSelection modal state
  const [showMasterSelection, setShowMasterSelection] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedContactType, setSelectedContactType] = useState(null);

  // Dark mode
  const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  useEffect(() => {
    const interval = setInterval(() => {
      setIsDarkMode(localStorage.getItem('darkMode') === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    fetchActivityLogs();
    fetchFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filterDate, filterCategory, filterItem, filterStatus]);

  // Dark mode styles for react-select
  const selectStyles = useMemo(() => ({
    control: (provided) => ({
      ...provided,
      minHeight: '40px',
      height: '40px',
      backgroundColor: isDarkMode ? '#374151' : 'white',
      borderColor: isDarkMode ? '#4b5563' : '#d1d5db',
      color: isDarkMode ? '#f3f4f6' : '#111827',
      '&:hover': { borderColor: isDarkMode ? '#6b7280' : '#9ca3af' }
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: '38px',
      padding: '0 8px'
    }),
    singleValue: (provided) => ({
      ...provided,
      color: isDarkMode ? '#f3f4f6' : '#111827'
    }),
    input: (provided) => ({
      ...provided,
      color: isDarkMode ? '#f3f4f6' : '#111827'
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? '#374151' : 'white',
      border: isDarkMode ? '1px solid #4b5563' : undefined
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? (isDarkMode ? '#4b5563' : '#eff6ff') : 'transparent',
      color: isDarkMode ? '#f3f4f6' : '#111827',
      '&:active': { backgroundColor: isDarkMode ? '#6b7280' : '#dbeafe' }
    }),
    placeholder: (provided) => ({
      ...provided,
      color: isDarkMode ? '#9ca3af' : '#9ca3af'
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? '#4b5563' : '#eff6ff',
      border: isDarkMode ? '1px solid #6b7280' : '1px solid #3b82f6'
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: isDarkMode ? '#e5e7eb' : '#1e40af'
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: isDarkMode ? '#e5e7eb' : '#1e40af',
      '&:hover': { backgroundColor: isDarkMode ? '#6b7280' : '#3b82f6', color: 'white' }
    })
  }), [isDarkMode]);

  const filterSelectStyles = useMemo(() => ({
    ...selectStyles,
    control: (provided) => ({
      ...provided,
      minHeight: '48px',
      height: '48px',
      backgroundColor: isDarkMode ? '#374151' : 'white',
      borderColor: isDarkMode ? '#4b5563' : '#d1d5db',
      borderRadius: '0.5rem',
      '&:hover': { borderColor: isDarkMode ? '#6b7280' : '#9ca3af' },
      '&:focus-within': { border: '2px solid #3b82f6', boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)' }
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: '46px',
      padding: '0 8px',
      display: 'flex',
      alignItems: 'center'
    })
  }), [isDarkMode, selectStyles]);

  const fetchActivityLogs = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        date: filterDate,
        category: filterCategory,
        item: filterItem,
        status: filterStatus,
        userId: user.user._id // Filter by current user's logs only
      };

      // Remove empty parameters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await axios.get(`${API_BASE}/activity-logs-new`, { params });
      
      if (response.data.success) {
        setActivityLogs(response.data.data);
        setTotalPages(response.data.pagination.totalPages || 1);
        setTotalCount(response.data.pagination.totalCount || 0);
        prepareChartData(response.data.data);
        preparePieChartData(response.data.data);
        calculateStatistics(response.data.data);
      } else {
        toast.error('Failed to fetch activity logs');
      }
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      toast.error('Failed to fetch activity logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API_BASE}/activity-logs-new/filters/options`);
      if (response.data.success) {
        setFilterOptions(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const prepareChartData = (logs) => {
    // Get last 7 days
    const last7Days = [];
    const today = new Date();
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      last7Days.push({
        date: date.toISOString().split('T')[0],
        label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      });
    }

    // Count logs per day
    const logCounts = last7Days.map(day => {
      const count = logs.filter(log => {
        const logDate = new Date(log.date).toISOString().split('T')[0];
        return logDate === day.date;
      }).length;
      return count;
    });

    setChartData({
      labels: last7Days.map(day => day.label),
      datasets: [
        {
          label: 'Activity Logs',
          data: logCounts,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.1,
          fill: true
        }
      ]
    });
  };

  const preparePieChartData = (logs) => {
    const statusCounts = {
      Pending: logs.filter(log => log.status === 'Pending').length,
      Completed: logs.filter(log => log.status === 'Completed').length,
      Cancelled: logs.filter(log => log.status === 'Cancelled').length,
      Draft: logs.filter(log => log.status === 'Draft').length,
      Service: logs.filter(log => log.status === 'Service').length,
      'Quotation issued': logs.filter(log => log.status === 'Quotation issued').length
    };

    setPieChartData({
      labels: ['Pending', 'Completed', 'Cancelled', 'Draft', 'Service', 'Quotation issued'],
      datasets: [
        {
          data: [statusCounts.Pending, statusCounts.Completed, statusCounts.Cancelled, statusCounts.Draft, statusCounts.Service, statusCounts['Quotation issued']],
          backgroundColor: ['#fbbf24', '#10b981', '#ef4444', '#6b7280', '#8b5cf6', '#3b82f6'],
          borderColor: ['#f59e0b', '#059669', '#dc2626', '#4b5563', '#7c3aed', '#2563eb'],
          borderWidth: 2,
        }
      ]
    });
  };

  const calculateStatistics = (logs) => {
    const stats = {
      total: logs.length,
      pending: 0,
      completed: 0,
      cancelled: 0,
      draft: 0,
      service: 0,
      quotationIssued: 0
    };
    logs.forEach(log => {
      switch (log.status) {
        case 'Pending':
          stats.pending++;
          break;
        case 'Completed':
          stats.completed++;
          break;
        case 'Cancelled':
          stats.cancelled++;
          break;
        case 'Draft':
          stats.draft++;
          break;
        case 'Service':
          stats.service++;
          break;
        case 'Quotation issued':
          stats.quotationIssued++;
          break;
        default:
          break;
      }
    });
    setStatistics(stats);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchActivityLogs();
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        date: formData.date.toISOString().split('T')[0], // Format date for API
        createdBy: user.user._id
      };

      const response = await axios.post(`${API_BASE}/activity-logs-new`, payload);
      
      if (response.data.success) {
        toast.success('Activity log created successfully');
        setShowCreateForm(false);
        setFormData({
          date: new Date(),
          category: '',
          items: [],
          guest: '',
          reference: '',
          customer: '',
          dealer: '',
          remarks: ''
        });
        setSelectedContact(null);
        setSelectedContactType(null);
        fetchActivityLogs();
      } else {
        toast.error(response.data.message || 'Failed to create activity log');
      }
    } catch (error) {
      console.error('Error creating activity log:', error);
      toast.error(error.response?.data?.message || 'Failed to create activity log');
    }
  };

  const handleExport = async () => {
    try {
      const params = {
        search: searchTerm,
        date: filterDate,
        category: filterCategory,
        item: filterItem,
        status: filterStatus
      };

      const response = await axios.get(`${API_BASE}/activity-logs-new/export/csv`, { 
        params,
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `activity-logs-new-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Activity logs exported successfully');
    } catch (error) {
      console.error('Error exporting activity logs:', error);
      toast.error('Failed to export activity logs');
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return {
      date: `${day}/${month}/${year}`,
      time: date.toLocaleTimeString()
    };
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'Pending': 'bg-yellow-100 text-yellow-800',
      'Completed': 'bg-green-100 text-green-800',
      'Cancelled': 'bg-red-100 text-red-800',
      'Draft': 'bg-gray-100 text-gray-800',
      'Service': 'bg-purple-100 text-purple-800',
      'Quotation issued': 'bg-blue-100 text-blue-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const getCategoryColor = (category) => {
    const categoryColors = {
      'Reference': 'bg-blue-100 text-blue-800',
      'Guest': 'bg-green-100 text-green-800',
      'Customer': 'bg-orange-100 text-orange-800',
      'Dealer': 'bg-purple-100 text-purple-800'
    };
    return categoryColors[category] || 'bg-gray-100 text-gray-800';
  };

  // Handle MasterSelection
  const handleMasterSelect = (item, entityType) => {
    setSelectedContact(item);
    setSelectedContactType(entityType);
    const fieldMap = { 'Reference': 'reference', 'Guest': 'guest', 'Customer': 'customer', 'Dealer': 'dealer' };
    const field = fieldMap[entityType];
    if (field) {
      setFormData(prev => ({
        ...prev,
        reference: '', guest: '', customer: '', dealer: '',
        [field]: item._id
      }));
    }
  };

  const getContactDisplayName = (item, type) => {
    if (!item) return '';
    switch (type) {
      case 'Guest': return `${item.guestName} - ${item.phoneNo || ''}`;
      case 'Reference': return `${item.name} - ${item.phoneNumber || ''} (${item.type || ''})`;
      case 'Customer': return `${item.name} - ${item.phone || ''}`;
      case 'Dealer': return `${item.businessName} - ${item.phone || ''}`;
      default: return '';
    }
  };

  return (
    <>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeInDown { from { opacity: 0; transform: translateY(-15px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.92); } to { opacity: 1; transform: scale(1); } }
        @keyframes slideInLeft { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slideInRight { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        .anim-fade-in { animation: fadeIn 0.5s ease-out both; }
        .anim-fade-in-up { animation: fadeInUp 0.5s ease-out both; }
        .anim-fade-in-down { animation: fadeInDown 0.4s ease-out both; }
        .anim-scale-in { animation: scaleIn 0.3s ease-out both; }
        .anim-slide-left { animation: slideInLeft 0.4s ease-out both; }
        .anim-slide-right { animation: slideInRight 0.4s ease-out both; }
        .anim-stagger-1 { animation-delay: 0.05s; }
        .anim-stagger-2 { animation-delay: 0.1s; }
        .anim-stagger-3 { animation-delay: 0.15s; }
        .anim-stagger-4 { animation-delay: 0.2s; }
        .anim-stagger-5 { animation-delay: 0.25s; }
        .anim-stagger-6 { animation-delay: 0.3s; }
        .anim-stagger-7 { animation-delay: 0.35s; }
        .anim-modal-backdrop { animation: fadeIn 0.2s ease-out both; }
        .anim-modal-content { animation: scaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) both; }
        .anim-table-row { animation: fadeInUp 0.3s ease-out both; }
      `}</style>
    <div className={`p-6 rounded-lg shadow-lg anim-fade-in ${isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-white'}`}>
      <div className="flex justify-between items-center mb-6 anim-fade-in-down">
        <h2 className={`text-2xl font-bold flex items-center ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
          <FaClock className="mr-3 text-blue-600" />
          Activity Logs (New)
        </h2>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-blue-700 transition-all duration-200 hover:scale-105 hover:shadow-lg"
          >
            <FaPlus className="mr-2" />
            Create Log
          </button>
          <button
            onClick={handleExport}
            className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-green-700 transition-all duration-200 hover:scale-105 hover:shadow-lg"
          >
            <FaDownload className="mr-2" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Create Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 anim-modal-backdrop">
          <div className={`p-6 rounded-lg max-w-md w-full mx-4 max-h-screen overflow-y-auto anim-modal-content ${isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white'}`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-gray-100' : ''}`}>Create Activity Log</h3>
              <button
                onClick={() => setShowCreateForm(false)}
                className={`${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <FaTimes />
              </button>
            </div>
            
            <form onSubmit={handleCreateSubmit} className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Date</label>
                <div className="relative">
                  <DatePicker
                    selected={formData.date}
                    onChange={(date) => setFormData({...formData, date: date})}
                    className={`w-full p-2 pl-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-10 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'border-gray-300'}`}
                    dateFormat="yyyy-MM-dd"
                    required
                  />
                  <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Category *</label>
                <Select
                  value={formData.category ? { value: formData.category, label: formData.category } : null}
                  onChange={(selectedOption) => {
                    const newCategory = selectedOption ? selectedOption.value : '';
                    setFormData({
                      ...formData, 
                      category: newCategory,
                      guest: '',
                      reference: '',
                      customer: '',
                      dealer: ''
                    });
                    setSelectedContact(null);
                    setSelectedContactType(null);
                  }}
                  options={[
                    { value: 'Reference', label: 'Reference' },
                    { value: 'Guest', label: 'Guest' },
                    { value: 'Customer', label: 'Customer' },
                    { value: 'Dealer', label: 'Dealer' }
                  ]}
                  placeholder="Select Category..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isClearable
                  styles={selectStyles}
                />
              </div>

              {/* Contact Selection via MasterSelection */}
              {formData.category && ['Reference', 'Guest', 'Customer', 'Dealer'].includes(formData.category) && (
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>{formData.category} *</label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowMasterSelection(true)}
                      className={`w-full px-3 py-2 border rounded-lg text-left focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        isDarkMode 
                          ? 'bg-gray-700 border-gray-600 hover:bg-gray-600 text-gray-100' 
                          : 'bg-white border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {selectedContact ? (
                        <span className={isDarkMode ? 'text-gray-100' : 'text-gray-900'}>
                          {getContactDisplayName(selectedContact, selectedContactType)}
                        </span>
                      ) : (
                        <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Select {formData.category}...</span>
                      )}
                    </button>
                    {selectedContact && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedContact(null);
                          setSelectedContactType(null);
                          setFormData(prev => ({ ...prev, reference: '', guest: '', customer: '', dealer: '' }));
                        }}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <FaTimes />
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Items *</label>
                <Select
                  isMulti
                  value={['Tiles', 'Sanitary Ware', 'Kithen', 'Doors', 'Windows', 'Others']
                    .filter(item => formData.items.includes(item))
                    .map(item => ({ value: item, label: item }))}
                  onChange={(selectedOptions) => {
                    const selectedItems = selectedOptions ? selectedOptions.map(option => option.value) : [];
                    setFormData({...formData, items: selectedItems});
                  }}
                  options={['Tiles', 'Sanitary Ware', 'Kithen', 'Doors', 'Windows', 'Others'].map(item => ({ value: item, label: item }))}
                  placeholder="Select Items..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={selectStyles}
                  required={formData.items.length === 0}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Remarks</label>
                <textarea
                  value={formData.remarks}
                  onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'border-gray-300'}`}
                  rows="3"
                  placeholder="Enter remarks (optional)"
                  style={{ minHeight: "80px" }}
                />
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className={`px-4 py-2 border rounded-lg ${isDarkMode ? 'text-gray-300 border-gray-600 hover:bg-gray-700' : 'text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Enhanced Filters Section */}
      <div className={`border rounded-xl shadow-sm mb-6 anim-fade-in-up anim-stagger-1 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-gray-700 bg-gradient-to-r from-gray-800 to-gray-750' : 'border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50'}`}>
          <div className="flex items-center justify-between">
            <h3 className={`text-lg font-semibold flex items-center ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
              <FaFilter className="mr-2 text-blue-600" />
              Search & Filter Activity Logs
            </h3>
            <div className="flex items-center space-x-2">
              <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {totalCount} total records
              </span>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterDate('');
                  setFilterCategory('');
                  setFilterItem('');
                  setFilterStatus('');
                  setCurrentPage(1);
                }}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center"
              >
                <FaTimes className="mr-1" />
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {/* Search Section */}
            <div className="xl:col-span-3">
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Search Activity Logs
              </label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by guest, reference, remarks, items..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 h-12 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'border-gray-300'}`}
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
            </div>

            {/* Date Filter */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <FaClock className="inline mr-1" />
                Filter by Date
              </label>
              <div className="relative">
                <DatePicker
                  selected={filterDate ? new Date(filterDate) : null}
                  onChange={(date) => setFilterDate(date ? date.toISOString().split('T')[0] : '')}
                  className={`w-full px-10 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 h-12 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'border-gray-300'}`}
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Select date..."
                  isClearable
                />
                <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                {filterDate && (
                  <button
                    onClick={() => setFilterDate('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
            </div>
            
            {/* Category Filter */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <FaTag className="inline mr-1" />
                Category
              </label>
              <Select
                value={filterCategory ? { value: filterCategory, label: filterCategory } : null}
                onChange={(selectedOption) => setFilterCategory(selectedOption ? selectedOption.value : '')}
                options={filterOptions.categories.map(category => ({ value: category, label: category }))}
                placeholder="All Categories"
                className="react-select-container"
                classNamePrefix="react-select"
                isClearable
                styles={filterSelectStyles}
              />
            </div>
            
            {/* Item Filter */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <FaBox className="inline mr-1" />
                Item
              </label>
              <Select
                value={filterItem ? { value: filterItem, label: filterItem } : null}
                onChange={(selectedOption) => setFilterItem(selectedOption ? selectedOption.value : '')}
                options={filterOptions.items.map(item => ({ value: item, label: item }))}
                placeholder="All Items"
                className="react-select-container"
                classNamePrefix="react-select"
                isClearable
                styles={filterSelectStyles}
              />
            </div>
            
            {/* Status Filter */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <FaCheckCircle className="inline mr-1" />
                Status
              </label>
              <Select
                value={filterStatus ? { value: filterStatus, label: filterStatus } : null}
                onChange={(selectedOption) => setFilterStatus(selectedOption ? selectedOption.value : '')}
                options={filterOptions.statuses.map(status => ({ value: status, label: status }))}
                placeholder="All Status"
                className="react-select-container"
                classNamePrefix="react-select"
                isClearable
                styles={filterSelectStyles}
              />
            </div>
            
            {/* Apply Filters Button */}
            <div className="flex items-end">
              <button
                onClick={handleSearch}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-lg flex items-center justify-center hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl active:scale-95"
              >
                <FaSearch className="mr-2" />
                Apply Filters
              </button>
            </div>
          </div>
          
          {/* Active Filters Display */}
          {(searchTerm || filterDate || filterCategory || filterItem || filterStatus) && (
            <div className={`mt-4 pt-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Active filters:</span>
                {searchTerm && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Search: "{searchTerm}"
                    <button
                      onClick={() => setSearchTerm('')}
                      className="ml-2 hover:text-blue-600"
                    >
                      <FaTimes />
                    </button>
                  </span>
                )}
                {filterDate && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Date: {(() => {
                      const date = new Date(filterDate);
                      const day = String(date.getDate()).padStart(2, '0');
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const year = date.getFullYear();
                      return `${day}/${month}/${year}`;
                    })()}
                    <button
                      onClick={() => setFilterDate('')}
                      className="ml-2 hover:text-green-600"
                    >
                      <FaTimes />
                    </button>
                  </span>
                )}
                {filterCategory && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    Category: {filterCategory}
                    <button
                      onClick={() => setFilterCategory('')}
                      className="ml-2 hover:text-purple-600"
                    >
                      <FaTimes />
                    </button>
                  </span>
                )}
                {filterItem && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    Item: {filterItem}
                    <button
                      onClick={() => setFilterItem('')}
                      className="ml-2 hover:text-yellow-600"
                    >
                      <FaTimes />
                    </button>
                  </span>
                )}
                {filterStatus && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Status: {filterStatus}
                    <button
                      onClick={() => setFilterStatus('')}
                      className="ml-2 hover:text-red-600"
                    >
                      <FaTimes />
                    </button>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Activity Logs Table */}
      <div className="overflow-x-auto anim-fade-in-up anim-stagger-2">
        <table className={`min-w-full border rounded-lg ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
            <tr>
              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                ID
              </th>
              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                <div className="flex items-center">
                  <FaClock className="mr-2" />
                  Date
                </div>
              </th>
              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                <div className="flex items-center">
                  <FaUser className="mr-2" />
                  Contact
                </div>
              </th>
              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                Category
              </th>
              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                Items
              </th>
              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                Status
              </th>
              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                Remarks
              </th>
            </tr>
          </thead>
          <tbody className={isDarkMode ? 'bg-gray-800 divide-y divide-gray-700' : 'bg-white divide-y divide-gray-200'}>
            {loading ? (
              <tr>
                <td colSpan="7" className="px-6 py-4 text-center">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-2">Loading activity logs...</span>
                  </div>
                </td>
              </tr>
            ) : activityLogs.length === 0 ? (
              <tr>
                <td colSpan="7" className={`px-6 py-4 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  No activity logs found
                </td>
              </tr>
            ) : (
              activityLogs.map((log, index) => {
                const { date } = formatDateTime(log.date);
                return (
                  <tr key={log._id || index} className={`${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} anim-table-row transition-colors duration-150`} style={{ animationDelay: `${index * 0.04}s` }}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                      {log.id}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                        {log.guest?.guestName || 
                         log.reference?.name ||
                         log.customer?.name ||
                         log.dealer?.businessName ||
                         'No Contact'}
                      </div>
                      {log.guest?.phoneNo && (
                        <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{log.guest.phoneNo}</div>
                      )}
                      {log.reference?.phoneNumber && (
                        <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{log.reference.phoneNumber}</div>
                      )}
                      {log.reference?.type && (
                        <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>({log.reference.type})</div>
                      )}
                      {log.customer?.phone && (
                        <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{log.customer.phone}</div>
                      )}
                      {log.dealer?.phone && (
                        <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{log.dealer.phone}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCategoryColor(log.category)}`}>
                        {log.category}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {Array.isArray(log.items) ? log.items.join(', ') : log.items || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(log.status)}`}>
                        {log.status}
                      </span>
                    </td>
                    <td className={`px-6 py-4 text-sm max-w-xs truncate ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {log.remarks}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6 anim-fade-in anim-stagger-3">
          <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-700'}`}>
            Showing page {currentPage} of {totalPages} ({totalCount} total records)
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`px-3 py-1 border rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? 'border-gray-600 text-gray-400 hover:text-gray-200' : 'border-gray-300 text-gray-500 hover:text-gray-700'}`}
            >
              Previous
            </button>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNumber = Math.max(1, currentPage - 2) + i;
              if (pageNumber > totalPages) return null;
              
              return (
                <button
                  key={pageNumber}
                  onClick={() => setCurrentPage(pageNumber)}
                  className={`px-3 py-1 border rounded-md text-sm font-medium ${
                    currentPage === pageNumber
                      ? 'bg-blue-600 text-white border-blue-600'
                      : isDarkMode ? 'border-gray-600 text-gray-400 hover:text-gray-200' : 'border-gray-300 text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {pageNumber}
                </button>
              );
            })}
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className={`px-3 py-1 border rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? 'border-gray-600 text-gray-400 hover:text-gray-200' : 'border-gray-300 text-gray-500 hover:text-gray-700'}`}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Statistics and Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Line Chart */}
        <div className={`p-6 rounded-lg shadow-lg anim-fade-in-up anim-stagger-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>Activity Logs - Last 7 Days</h3>
          <div className="h-64">
            <Line 
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top',
                    labels: { color: isDarkMode ? '#e5e7eb' : '#374151' }
                  },
                  title: {
                    display: false,
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      stepSize: 1,
                      color: isDarkMode ? '#9ca3af' : '#6b7280'
                    },
                    grid: { color: isDarkMode ? '#374151' : '#e5e7eb' }
                  },
                  x: {
                    ticks: { color: isDarkMode ? '#9ca3af' : '#6b7280' },
                    grid: { color: isDarkMode ? '#374151' : '#e5e7eb' }
                  }
                },
              }}
            />
          </div>
        </div>

        {/* Pie Chart with Statistics */}
        <div className={`p-6 rounded-lg shadow-lg anim-fade-in-up anim-stagger-5 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>Status Distribution</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="h-48">
              <Pie 
                data={pieChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: {
                        boxWidth: 12,
                        padding: 10,
                        color: isDarkMode ? '#e5e7eb' : '#374151'
                      }
                    },
                  },
                }}
              />
            </div>
            <div className="space-y-3">
              <div className={`flex justify-between items-center p-2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Total</span>
                <span className="text-lg font-bold text-blue-600">{statistics.total}</span>
              </div>
              <div className={`flex justify-between items-center p-2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-yellow-50'}`}>
                <span className={`text-sm font-medium ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>Pending</span>
                <span className={`text-lg font-bold ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>{statistics.pending}</span>
              </div>
              <div className={`flex justify-between items-center p-2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-green-50'}`}>
                <span className={`text-sm font-medium ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>Completed</span>
                <span className={`text-lg font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>{statistics.completed}</span>
              </div>
              <div className={`flex justify-between items-center p-2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-red-50'}`}>
                <span className={`text-sm font-medium ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}>Cancelled</span>
                <span className={`text-lg font-bold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{statistics.cancelled}</span>
              </div>
              <div className={`flex justify-between items-center p-2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Draft</span>
                <span className={`text-lg font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{statistics.draft}</span>
              </div>
              <div className={`flex justify-between items-center p-2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-purple-50'}`}>
                <span className={`text-sm font-medium ${isDarkMode ? 'text-purple-400' : 'text-purple-700'}`}>Service</span>
                <span className={`text-lg font-bold ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>{statistics.service}</span>
              </div>
              <div className={`flex justify-between items-center p-2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
                <span className={`text-sm font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>Quotation Issued</span>
                <span className={`text-lg font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{statistics.quotationIssued}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MasterSelection Modal */}
      <MasterSelection
        isOpen={showMasterSelection}
        onClose={() => setShowMasterSelection(false)}
        onSelect={handleMasterSelect}
        allowedTypes={formData.category ? [formData.category] : ['Reference', 'Guest', 'Customer', 'Dealer']}
        defaultType={formData.category || 'Customer'}
        title={`Select ${formData.category || 'Contact'}`}
      />
    </div>
    </>
  );
};

export default ActivityLogsNew;
